import { getParameterByName, isObject, isObjectEmpty, isSafari, toQueryString } from '@va/util/helpers';
import 'moment-timezone';
import { createException } from './Exceptions';
import BaseConfig from './config';

// TODO Move to constants
export const EXPORT_FORMAT = {
  CSV: 'csv',
  XLSX: 'xlsx',
};

// TODO Move to constants
export const urlViews = {
  URL: 'url',
  TITLE: 'title',
};

export const jsonRequestHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const readOnly = getParameterByName('read-only');

export const checkStatus = (response: any) => {
  if (response.status >= 500 && response.status <= 599) {
    throw new Error(response.status);
  }

  if (response.ok) {
    // return data or json deserialization error
    return response.status === 202 || response.status === 204 ? {} : response.json();
  }

  // return error message from api or default http text
  return response.json().then(
    (json: any) => {
      if (json.error === 'AUTHENTICATION_EXPIRED_JWT_TOKEN') {
        // reloading the window triggers another initialize -> refreshing the auth token
        window.location.reload();
        return;
      }
      throw createException(json.error);
    },
    () => {
      throw new Error(response.statusText, response.status);
    },
  );
};

const call = (url: string, method: string, data: any, options?: Record<string, any>, containsFiles?: boolean) => {
  const baseUrl = BaseConfig.getApiBaseUrl();
  url = baseUrl.replace(/\/?$/, '').concat(url);
  const authHeaders = BaseConfig.getAuthProvider()();

  const requestHeaders: Record<string, any> = containsFiles
    ? { ...authHeaders }
    : {
        ...authHeaders,
        ...jsonRequestHeaders,
        'X-Timezone': window.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
  const request: Record<string, unknown> = {
    method: method,
  };

  if (options?.headers && isObject(options.headers)) {
    for (const key in options.headers) {
      requestHeaders[key] = options.headers[key];
    }
    delete options.headers;
  }

  request['headers'] = requestHeaders;

  if (options && isObject(options)) {
    // copy all keys from options
    Object.keys(options).forEach((key) => (request[key] = options[key]));
  }

  if (containsFiles) {
    const formData = new FormData();

    for (const name in data) {
      if (name !== 'files') formData.append(name, data[name]);
    }

    if (data.files) {
      for (let i = 0; i < data.files.length; i++) {
        formData.append(`file-${i}`, data.files[i]);
      }
    }

    request.body = formData;
  } else if (Array.isArray(data) || (isObject(data) && method.toUpperCase() !== 'GET')) {
    request.body = JSON.stringify(data);
  }

  if (readOnly) {
    // add read-only parameter to url query string
    url = url.includes('?') ? `${url}&read-only=${readOnly}` : `${url}?read-only=${readOnly}`;
  }

  return fetch(url, request).then((res) => checkStatus(res));
};

function dataURItoBlob(dataURI: string) {
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0) byteString = atob(dataURI.split(',')[1]);
  else byteString = unescape(dataURI.split(',')[1]);

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ia], { type: mimeString });
}

const callWithBase64 = (url: string, method: string, data: any, options?: Record<string, any>) => {
  url = BaseConfig.getApiBaseUrl().replace(/\/?$/, '').concat(url);
  const authHeaders = BaseConfig.getAuthProvider()();
  const requestHeaders = { ...authHeaders };
  const request: Record<string, any> = {
    method: method,
    headers: requestHeaders,
  };

  if (options && isObject(options)) {
    // copy all keys from options
    Object.keys(options).forEach((key) => (request[key] = options[key]));
  }

  const formData = new FormData();
  const file = dataURItoBlob(data.signatureImage);

  formData.append('signatureImage', file);

  request.body = formData;

  if (readOnly) {
    // add read-only parameter to url query string
    url = url.includes('?') ? `${url}&read-only=${readOnly}` : `${url}?read-only=${readOnly}`;
  }

  return fetch(url, request).then(checkStatus);
};

export const get = <T = unknown>(
  url: string,
  queryData?: Record<string, any>,
  options?: Record<string, any>,
  isDirect?: boolean,
): Promise<T> => {
  if (isDirect) {
    const request = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };
    return fetch(url, request).then(checkStatus);
  }
  queryData = queryData || {};
  return call(`${url}${!isObjectEmpty(queryData) ? '?' + toQueryString(queryData) : ''}`, 'GET', {}, options);
};

export const post = <T = unknown>(
  url: string,
  queryData: Record<string, unknown>,
  data: any,
  options?: Record<string, any>,
): Promise<T> => {
  data = data || {};
  const containsFiles = (data.files && data.files.length > 0) || data.file;

  return call(
    `${url}${!isObjectEmpty(queryData) ? '?' + toQueryString(queryData) : ''}`,
    'POST',
    data,
    options,
    containsFiles,
  );
};

export const postBase64 = (
  url: string,
  queryData: Record<string, unknown>,
  data: any,
  options?: Record<string, any>,
) => {
  data = data || {};
  return callWithBase64(
    `${url}${!isObjectEmpty(queryData) ? '?' + toQueryString(queryData) : ''}`,
    'POST',
    data,
    options,
  );
};

export const patch = <T = unknown>(
  url: string,
  queryData?: Record<string, unknown>,
  data?: any,
  options?: Record<string, unknown>,
): Promise<T> => {
  data = data || {};
  return call(`${url}${!isObjectEmpty(queryData) ? '?' + toQueryString(queryData) : ''}`, 'PATCH', data, options);
};

export const put = <T = unknown>(
  url: string,
  queryData: Record<string, unknown>,
  data: any,
  options?: Record<string, unknown>,
): Promise<T> => {
  data = data || {};
  return call(`${url}${!isObjectEmpty(queryData) ? '?' + toQueryString(queryData) : ''}`, 'PUT', data, options);
};

export const remove = <T = unknown>(
  url: string,
  queryData?: Record<string, unknown>,
  data?: any,
  options?: Record<string, unknown>,
): Promise<T> => {
  data = data || {};
  queryData = queryData || {};
  return call(`${url}${!isObjectEmpty(queryData) ? '?' + toQueryString(queryData) : ''}`, 'DELETE', data, options);
};

export function downloadResponse(data: string, filename: string, mime = 'text/csv') {
  return new Promise((resolve, reject) => {
    const blob = new Blob([data], { type: mime });
    if (isSafari(window.navigator.userAgent)) {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = function () {
        const base64data = reader.result as string;

        if (!base64data) {
          reject();
          return;
        }

        resolve({
          href: 'data:attachment/csv' + base64data.slice(base64data.search(/[,;]/)),
          download: filename,
        });
      };
    } else {
      const blobURL = window.URL.createObjectURL(blob);
      downloadData(blobURL, filename);
      resolve(void 0);
    }
  });
}

export function downloadData(dataURL: string, filename: string) {
  const tempLink = document.createElement('a');
  document.body.appendChild(tempLink);
  tempLink.setAttribute('href', dataURL);
  tempLink.setAttribute('download', filename);
  tempLink.click();
  document.body.removeChild(tempLink);
}

export const getPollLogoUrl = (websiteId: string, pollId: string) => {
  return `${BaseConfig.getApiBaseUrl()}/websites/${websiteId}/polls/${pollId}/logo`;
};

export const getAgencyLogoUrl = (userId: string, agencyId: string) => {
  return `${BaseConfig.getApiBaseUrl()}/users/${userId}/agencyui/${agencyId}/logo?_=${+new Date()}`;
};

export const getSurveyLogoUrl = (websiteId: string, surveyId: string) => {
  return `${BaseConfig.getApiBaseUrl()}/websites/${websiteId}/surveys/${surveyId}/logo`;
};

export function getDownloadAgreementLink(token: string, fileName: string) {
  const apiBaseUrl = BaseConfig.getApiBaseUrl();
  return apiBaseUrl + `/agreements/download/${fileName}?access-token=${token}`;
}

export function isServerError(status: number) {
  if (!status) return false;
  return status >= 500 && status <= 599;
}
